import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, Info } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((messageOrOptions, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    const toast = typeof messageOrOptions === 'object' && messageOrOptions !== null
      ? {
          id,
          type: messageOrOptions.type || 'info',
          title: messageOrOptions.title || null,
          message: messageOrOptions.message || '',
          actionLabel: messageOrOptions.actionLabel || null,
          onAction: messageOrOptions.onAction || null
        }
      : { id, message: messageOrOptions, type };
    setToasts(prev => [...prev, toast]);

    const autoDismiss = typeof messageOrOptions === 'object' && messageOrOptions !== null
      ? (messageOrOptions.duration ?? 3000)
      : duration;

    if (autoDismiss > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, autoDismiss);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({ toast, onRemove }) => {
  const { message, type, title, actionLabel, onAction } = toast;
  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
  const Icon = type === 'success' ? CheckCircle2 : type === 'error' ? XCircle : Info;

  return (
    <div className={`${bgColor} text-white px-4 py-3 rounded-lg shadow-xl flex items-start gap-3 min-w-[320px] max-w-md animate-slide-in`}>
      <Icon size={20} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-semibold leading-tight">{title}</p>}
        <p className="text-sm font-medium break-words">{message}</p>
        {actionLabel && typeof onAction === 'function' && (
          <button
            onClick={() => {
              onAction();
              onRemove(toast.id);
            }}
            className="mt-2 text-xs font-semibold underline underline-offset-2 hover:opacity-90"
          >
            {actionLabel}
          </button>
        )}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="text-white hover:text-gray-200 font-bold text-xl leading-none flex-shrink-0 w-5 h-5 flex items-center justify-center"
      >
        ×
      </button>
    </div>
  );
};

